import React from 'react'
import { BaseEntity } from '../models/ApiModels'

// Generic base interface for table items
/*export interface BaseTableItem {
  id: string | number
}*/

// Column configuration interface
export interface TableColumn<T> {
  key: keyof T | string
  label: string
  render?: (value: any, row: T) => React.ReactNode;
  //render?: (item: T, value: any) => React.ReactNode
  sortable?: boolean
  className?: string
  width?: string
  align?: 'left' | 'center' | 'right';
  hidden?: boolean;
}

// Action button interface
export interface TableAction<T> {
  label: string
  icon?: string
  className?: string
  onClick: (item: T) => void
  condition?: (item: T) => boolean // Show/hide condition
  disabled?: (item: T) => boolean // Enable/disable condition
}

// Component props interface
interface DataTableProps<T extends BaseEntity> {
  data: T[]
  columns: TableColumn<T>[]
  actions?: TableAction<T>[]
  currentPage?: number
  totalPages?: number
  pageSize?: number
  pageSizeOptions?: number[]
  onPageSizeChange?: (size: number) => void
  onPageChange?: (page: number) => void
  loading?: boolean
  emptyMessage?: string
  className?: string
  tableClassName?: string
  showPagination?: boolean
  showPageInfo?: boolean
  paginationSize?: 'sm' | 'md' | 'lg'
  striped?: boolean
  bordered?: boolean
  hover?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (column: string, order: 'asc' | 'desc') => void
}

const DataTable = <T extends BaseEntity>({
  data,
  columns,
  actions = [],
  currentPage = 0,
  totalPages = 1,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  onPageChange,
  loading = false,
  emptyMessage = 'Kayıt bulunamadı',
  className = '',
  tableClassName = '',
  showPagination = true,
  showPageInfo = true,
  paginationSize = 'sm',
  striped = true,
  bordered = true,
  hover = false,
  sortBy,
  sortOrder,
  onSort
}: DataTableProps<T>) => {
  
  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages && onPageChange) {
      onPageChange(page)
    }
  }

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !onSort) return
    
    const columnKey = column.key as string
    const newOrder = sortBy === columnKey && sortOrder === 'asc' ? 'desc' : 'asc'
    onSort(columnKey, newOrder)
  }

  const renderCellValue = (item: T, column: TableColumn<T>) => {
    const value = column.key.toString().includes('.') 
      ? column.key.toString().split('.').reduce((obj, key) => obj?.[key], item as any)
      : (item as any)[column.key]
    
    if (column.render) {
      return column.render(value, item)
    }
    
    return value ?? '-'
  }

  const getSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable) return null
    
    const columnKey = column.key as string
    if (sortBy !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>
    }
    
    return sortOrder === 'asc' 
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>
  }

  const getTableClasses = () => {
    let classes = 'table'
    if (striped) classes += ' table-striped'
    if (bordered) classes += ' table-row-bordered'
    if (hover) classes += ' table-hover'
    classes += ' gy-5 gs-7'
    if (tableClassName) classes += ` ${tableClassName}`
    return classes
  }

  const getPaginationSize = () => {
    return paginationSize === 'sm' ? 'btn-sm' : paginationSize === 'lg' ? 'btn-lg' : ''
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center p-5'>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Yükleniyor...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Table */}
      <div className="table-responsive">
        <table className={getTableClasses()}>
          <thead>
            <tr className='fw-bold fs-6 text-gray-800'>
              {columns.map((column, index) => (
                <th 
                  key={index}
                  className={`${column.className || ''} ${column.sortable ? 'cursor-pointer user-select-none' : ''}`}
                  style={{ width: column.width }}
                  onClick={() => handleSort(column)}
                >
                  <div className="d-flex align-items-center">
                    {column.label}
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
              {actions.length > 0 && <th>İşlemler</th>}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)} className='text-center py-5 text-muted'>
                  <div className="d-flex flex-column align-items-center">
                    <i className="bi bi-inbox fs-1 mb-3 text-muted"></i>
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.id}>
                  {columns.map((column, columnIndex) => (
                    <td key={columnIndex} className={column.className}>
                      {renderCellValue(item, column)}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td>
                      <div className='d-flex gap-2'>
                        {actions
                          .filter(action => !action.condition || action.condition(item))
                          .map((action, actionIndex) => (
                            <button
                              key={actionIndex}
                              className={`btn btn-icon ${getPaginationSize()} ${action.className || 'btn-light-primary'}`}
                              onClick={() => action.onClick(item)}
                              title={action.label}
                              type='button'
                              disabled={action.disabled ? action.disabled(item) : false}
                            >
                              {action.icon ? (
                                <i className={action.icon}></i>
                              ) : (
                                action.label
                              )}
                            </button>
                          ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {showPagination && totalPages > 1 && onPageChange && (
        <div className='d-flex justify-content-between align-items-center mt-5'>
          {/* Page size selector */}
          {onPageSizeChange && (
            <div className='d-flex align-items-center'>
              <select
                className={`form-select form-select-${paginationSize} w-auto`}
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className='d-flex align-items-center gap-1'>
          <button
            className={`btn ${getPaginationSize()} btn-light`}
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
            type='button'
          >
            <i className='bi bi-chevron-left me-1'></i>
            Önceki
          </button>

          {/* Page numbers */}
          <div className='d-flex align-items-center gap-1'>
            {/* First page */}
            {currentPage > 1 && (
              <button
                className={`btn ${getPaginationSize()} btn-light`}
                onClick={() => handlePageChange(0)}
                type='button'
              >
                1
              </button>
            )}
            
            {currentPage > 2 && <span className='px-2'>...</span>}
            
            {/* Previous page */}
            {currentPage > 0 && (
              <button
                className={`btn ${getPaginationSize()} btn-light`}
                onClick={() => handlePageChange(currentPage - 1)}
                type='button'
              >
                {currentPage}
              </button>
            )}
            
            {/* Current page */}
            <button className={`btn ${getPaginationSize()} btn-primary`} disabled>
              {currentPage + 1}
            </button>
            
            {/* Next page */}
            {currentPage < totalPages - 1 && (
              <button
                className={`btn ${getPaginationSize()} btn-light`}
                onClick={() => handlePageChange(currentPage + 1)}
                type='button'
              >
                {currentPage + 2}
              </button>
            )}
            
            {currentPage < totalPages - 3 && <span className='px-2'>...</span>}
            
            {/* Last page */}
            {currentPage < totalPages - 2 && (
              <button
                className={`btn ${getPaginationSize()} btn-light`}
                onClick={() => handlePageChange(totalPages - 1)}
                type='button'
              >
                {totalPages}
              </button>
            )}
          </div>

          <button
            className={`btn ${getPaginationSize()} btn-light`}
            disabled={currentPage >= totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
            type='button'
          >
            Sonraki
            <i className='bi bi-chevron-right ms-1'></i>
          </button>
          </div>
        </div>
      )}

      {/* Page info */}
      {showPageInfo && data.length > 0 && (
        <div className='d-flex justify-content-center mt-3'>
          <small className='text-muted'>
            Toplam {data.length} kayıt 
            {totalPages > 1 && ` | Sayfa ${currentPage + 1} / ${totalPages}`}
          </small>
        </div>
      )}
    </div>
  )
}

export default DataTable